import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  TextField,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
  useTheme,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import ApiService from '../../services/api.service';
import useOrganizationSearch from '../../hooks/useOrganizationSearch';
import internalServiceService from '../../services/internal-service.service';

// Compact 13px form fields, like the rest of the app.
const FORM_SX = {
  '& .MuiInputBase-input': { fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem' },
  '& .MuiFormHelperText-root': { fontSize: '0.6875rem' },
};

// Tax Configuration - UAE VAT Rate (can be changed for different countries)
const VAT_RATE = 0.05; // 5% UAE VAT
const VAT_RATE_DISPLAY = `${VAT_RATE * 100}%`; // "5%"

const CreateQuotation = ({ currentUser, isEditMode = false }) => {
  const navigate = useNavigate();
  const { quotationId } = useParams();
  // Walk-in is a Thaklis feature: the customer-type toggle is shown only when Thaklis features are
  // enabled (a Thaklis-disabled B2B has organization customers only, and the toggle defaults there).
  const thaklisDisabled = currentUser?.thaklisEnabled === false;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(isEditMode);

  // Form states
  const [customerType, setCustomerType] = useState('organization'); // 'organization' or 'walkin'
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const {
    options: organizations,
    loading: loadingOrgs,
    onInputChange: onOrganizationInputChange,
    filterOptions: organizationFilterOptions
  } = useOrganizationSearch({
    typingCenterId: currentUser?.typingCenterId || currentUser?.typing_center_id,
    mapOption: (org) => ({
      id: org.id,
      name: org.name || org.organization_name,
      code: org.code || org.organization_code || '',
      contact_person: org.contact_person || org.contactPerson || '',
      email: org.email || '',
      phone: org.phone || org.contact_number || '',
      type: 'internal'
    })
  });

  // Mapped Thaklis organization (which Thaklis org the applicants belong to) —
  // same picker as the work-order create page; always shown, both customer types.
  // Carried onto the work order on conversion, and from there onto the invoice.
  const [mappedOrganizations, setMappedOrganizations] = useState([]);
  const [selectedMappedOrg, setSelectedMappedOrg] = useState(null);
  const [loadingMappedOrgs, setLoadingMappedOrgs] = useState(false);
  const [quotationDate, setQuotationDate] = useState(new Date().toISOString().split('T')[0]);
  const [validUntil, setValidUntil] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() + 14); // Default 14 days validity
    return date.toISOString().split('T')[0];
  });
  const [notes, setNotes] = useState('');
  const [termsAndConditions, setTermsAndConditions] = useState('Payment due within 30 days of acceptance.');

  // Customer/Applicant details
  const [applicantName, setApplicantName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [organizationNameOverride, setOrganizationNameOverride] = useState('');

  // Line items
  const [lineItems, setLineItems] = useState([]);

  // Service selection dialog
  const [serviceDialogOpen, setServiceDialogOpen] = useState(false);
  const [availableServices, setAvailableServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [customServiceName, setCustomServiceName] = useState('');
  const [customServiceCharge, setCustomServiceCharge] = useState('');
  const [customGovtCharge, setCustomGovtCharge] = useState('');
  const [isCustomService, setIsCustomService] = useState(false);
  const [markupPercent, setMarkupPercent] = useState('');

  // Fetch organizations on mount or when currentUser changes
  useEffect(() => {
    if (currentUser) {
      fetchMappedOrganizations();
    }
  }, [currentUser]);

  // (Re)load addable services whenever the customer/org changes — internal-service prices are resolved
  // for the selected organization (walk-in / no org → uniform services only).
  useEffect(() => {
    if (currentUser) {
      fetchServices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, customerType, selectedOrganization]);

  // Fetch quotation data in edit mode
  useEffect(() => {
    if (isEditMode && quotationId && organizations.length > 0) {
      fetchQuotation();
    }
  }, [isEditMode, quotationId, organizations]);

  const fetchQuotation = async () => {
    setLoading(true);
    try {
      const response = await ApiService.getQuotationById(quotationId);
      const quotation = response?.data?.data || response?.data;
      console.log('Fetched quotation data:', quotation);

      if (quotation) {
        // Set organization
        const orgId = quotation.organization_id || quotation.organizationId;
        console.log('Looking for org with id:', orgId, 'in', organizations.length, 'organizations');
        const org = organizations.find(o => o.id === orgId);
        console.log('Found org:', org);
        if (org) {
          setSelectedOrganization(org);
        }

        // Set dates
        setQuotationDate(quotation.quotation_date || quotation.quotationDate || new Date().toISOString().split('T')[0]);
        setValidUntil(quotation.valid_until || quotation.validUntil || '');

        // Set notes
        setNotes(quotation.notes || '');
        setTermsAndConditions(quotation.terms_and_conditions || quotation.termsAndConditions || '');

        // Set customer type and details
        const hasOrganization = quotation.organization_id || quotation.organizationId;
        setCustomerType(hasOrganization ? 'organization' : 'walkin');

        const mappedOrgId = quotation.mapped_organization_id || quotation.mappedOrganizationId;
        const mappedOrgName = quotation.mapped_organization_name || quotation.mappedOrganizationName;
        if (mappedOrgId) {
          setSelectedMappedOrg(prev => prev && prev.id === mappedOrgId
            ? prev
            : { id: mappedOrgId, name: mappedOrgName || '', code: '' });
        }

        // Get customer/applicant name (fallback to organization contact if empty)
        const name = quotation.customer_name || quotation.customerName || org?.contact_person || '';
        console.log('Setting applicantName to:', name);
        setApplicantName(name);

        setCustomerEmail(quotation.customer_email || quotation.customerEmail || '');
        setCustomerPhone(quotation.customer_phone || quotation.customerPhone || '');
        setOrganizationNameOverride(quotation.organization_name || quotation.organizationName || '');

        // Set line items
        const items = (quotation.line_items || quotation.lineItems || []).map((item, index) => ({
          id: item.id || `temp-${Date.now()}-${index}`,
          service_id: item.service_id || item.serviceId,
          service_name: item.service_name || item.serviceName,
          service_description: item.service_description || item.serviceDescription || '',
          quantity: item.quantity || 1,
          unit_price: item.unit_price || item.unitPrice || 0,
          service_charge: item.service_charge || item.serviceCharge || 0,
          govt_charge: item.govt_charge || item.govtCharge || 0,
          tax_value: item.tax_value || item.taxValue || 0,
          tax_on_markup: item.tax_on_markup || item.taxOnMarkup || 0,
          line_total: item.line_total || item.lineTotal || 0,
          original_line_total: item.original_line_total || item.originalLineTotal || 0,
          markup_percent: item.markup_percent || item.markupPercent,
          markup_amount: item.markup_amount || item.markupAmount || 0,
          is_custom_service: item.is_custom_service || item.isCustomService || false
        }));
        setLineItems(items);
      }
    } catch (err) {
      console.error('Error fetching quotation:', err);
      setError('Failed to load quotation');
    } finally {
      setLoading(false);
    }
  };

  const fetchMappedOrganizations = async () => {
    setLoadingMappedOrgs(true);
    try {
      const typingCenterId = currentUser?.typingCenterId || currentUser?.typing_center_id;
      const response = await ApiService.getMappings(typingCenterId);
      const orgs = (response?.data?.data || []).map(m => ({
        id: m.organization_id,
        name: m.organization_name,
        code: m.organization_code || ''
      }));
      setMappedOrganizations(orgs);
      // Auto-select when exactly one mapping (mirrors the work-order create page).
      if (orgs.length === 1) {
        setSelectedMappedOrg(orgs[0]);
      }
    } catch (err) {
      console.error('Error fetching mapped organizations:', err);
    } finally {
      setLoadingMappedOrgs(false);
    }
  };

  const fetchServices = async () => {
    try {
      // Resolve internal-service prices for the selected org; walk-in / no org → uniform services only.
      const orgId = customerType === 'organization' ? (selectedOrganization?.id || null) : null;
      const allServices = await internalServiceService.getServicesForLineItems(orgId);
      const services = allServices.map(s => ({
        id: s.id,
        name: s.name || s.service_name,
        description: s.description || '',
        service_charge: s.service_charge || s.serviceCharge || 0,
        tax_on_service_charge: s.tax_on_service_charge || s.taxOnServiceCharge || 0,
        govt_charge: s.govt_charge || s.govtCharge || 0,
        type: s.type || 'THAKLIS'
      }));
      setAvailableServices(services);
    } catch (err) {
      console.error('Error fetching services:', err);
      setAvailableServices([]);
    }
  };

  const handleOrganizationChange = (event, value) => {
    setSelectedOrganization(value);
    // Pre-fill contact details and name from organization (all remain editable).
    if (value) {
      setOrganizationNameOverride(value.name || '');
      setCustomerEmail(value.email || '');
      setCustomerPhone(value.phone || '');
    } else {
      setOrganizationNameOverride('');
      setCustomerEmail('');
      setCustomerPhone('');
    }
  };

  const handleCustomerTypeChange = (type) => {
    setCustomerType(type);
    // Reset fields when switching
    if (type === 'walkin') {
      setSelectedOrganization(null);
      setOrganizationNameOverride('');
      setCustomerEmail('');
      setCustomerPhone('');
    }
    setApplicantName('');
  };

  const handleAddService = () => {
    // Markup is always inclusive of VAT — split to base + tax
    const markupInclVat = parseFloat(markupPercent) || 0;
    const markupBase = Number((markupInclVat / 1.05).toFixed(2));
    const markupVat = Number((markupInclVat - markupBase).toFixed(2));

    let serviceCharge, govtCharge, serviceTax, serviceName, serviceDescription, serviceId, isCustom;

    if (isCustomService) {
      if (!customServiceName || !customServiceCharge) {
        setError('Please enter service name and charge');
        return;
      }
      // Custom service — user entered inclusive amount, auto-split
      const inclVat = parseFloat(customServiceCharge) || 0;
      serviceCharge = Number((inclVat / 1.05).toFixed(2));
      serviceTax = Number((inclVat - serviceCharge).toFixed(2));
      govtCharge = parseFloat(customGovtCharge) || 0;
      serviceName = customServiceName;
      serviceDescription = '';
      serviceId = null;
      isCustom = true;
    } else {
      if (!selectedService) {
        setError('Please select a service');
        return;
      }
      // Catalog service — already has exclusive service_charge + tax_on_service_charge
      serviceCharge = selectedService.service_charge || 0;
      serviceTax = selectedService.tax_on_service_charge || 0;
      govtCharge = selectedService.govt_charge || 0;
      serviceName = selectedService.name;
      serviceDescription = selectedService.description || '';
      serviceId = selectedService.id;
      // Internal service: isCustomService=true + serviceId set; Thaklis: false
      isCustom = selectedService.type === 'INTERNAL';
    }

    // line_total = all-inclusive; original_line_total = base with tax, no markup
    const lineTotal = serviceCharge + govtCharge + markupBase + serviceTax + markupVat;
    const originalLineTotal = serviceCharge + govtCharge + serviceTax;

    const newLineItem = {
      id: `temp-${Date.now()}`,
      service_id: serviceId,
      service_name: serviceName,
      service_description: serviceDescription,
      quantity: 1,
      unit_price: serviceCharge + govtCharge,
      service_charge: serviceCharge,
      govt_charge: govtCharge,
      tax_value: serviceTax,
      tax_on_markup: markupVat,
      line_total: lineTotal,
      original_line_total: originalLineTotal,
      markup_percent: null,
      markup_amount: markupBase,
      is_custom_service: isCustom
    };

    setLineItems(prev => [...prev, newLineItem]);

    // Reset dialog
    setServiceDialogOpen(false);
    setSelectedService(null);
    setCustomServiceName('');
    setCustomServiceCharge('');
    setCustomGovtCharge('');
    setIsCustomService(false);
    setMarkupPercent('');
  };

  const handleRemoveLineItem = (lineItemId) => {
    setLineItems(prev => prev.filter(item => item.id !== lineItemId));
  };

  const calculateTotals = () => {
    // subtotal = sum(sc + gc + markup) × qty — no tax
    const subtotal = lineItems.reduce((sum, item) =>
      sum + (item.service_charge || 0) + (item.govt_charge || 0) + (item.markup_amount || 0), 0);
    // taxTotal = sum(tax + taxOnMarkup) — full tax
    const taxTotal = lineItems.reduce((sum, item) =>
      sum + (item.tax_value || 0) + (item.tax_on_markup || 0), 0);
    const grandTotal = subtotal + taxTotal;
    return { subtotal, taxTotal, grandTotal };
  };

  const handleCreateQuotation = async () => {
    // Validation based on customer type
    if (customerType === 'organization' && !selectedOrganization) {
      setError('Please select an organization');
      return;
    }

    if (!applicantName.trim()) {
      setError('Please enter applicant name');
      return;
    }

    if (lineItems.length === 0) {
      setError('Please add at least one service');
      return;
    }

    setCreating(true);
    setError(null);

    try {
      const { subtotal, taxTotal, grandTotal } = calculateTotals();

      // Get typing center ID
      const typingCenterId = currentUser?.typingCenterId || currentUser?.typing_center_id;

      const quotationData = {
        typing_center_id: typingCenterId,
        organization_id: customerType === 'organization' ? selectedOrganization?.id : null,
        organization_name: customerType === 'organization'
          ? (organizationNameOverride?.trim() || selectedOrganization?.name || null)
          : null,
        organization_code: customerType === 'organization' ? selectedOrganization?.code : null,
        mapped_organization_id: selectedMappedOrg?.id || null,
        mapped_organization_name: selectedMappedOrg?.name || null,
        customer_type: customerType,
        customer_name: applicantName,
        customer_email: customerEmail,
        customer_phone: customerPhone,
        quotation_date: quotationDate,
        valid_until: validUntil,
        subtotal,
        tax_total: taxTotal,
        grand_total: grandTotal,
        item_count: lineItems.length,
        terms_and_conditions: termsAndConditions,
        notes,
        line_items: lineItems.map((item, index) => ({
          service_id: item.service_id,
          service_name: item.service_name,
          service_description: item.service_description || '',
          quantity: item.quantity,
          unit_price: item.unit_price,
          service_charge: item.service_charge,
          govt_charge: item.govt_charge,
          tax_value: item.tax_value,
          tax_on_markup: item.tax_on_markup,
          line_total: item.line_total,
          original_line_total: item.original_line_total,
          markup_percent: item.markup_percent,
          markup_amount: item.markup_amount,
          is_custom_service: item.is_custom_service,
          sort_order: index
        }))
      };

      let response;
      if (isEditMode && quotationId) {
        response = await ApiService.updateQuotation(quotationId, quotationData);
        console.log('Quotation updated:', response?.data);
        setSuccess('Quotation updated successfully!');
      } else {
        response = await ApiService.createQuotation(quotationData);
        console.log('Quotation created:', response?.data);
        setSuccess('Quotation created successfully!');
      }

      setTimeout(() => {
        navigate('/quotations');
      }, 1500);
    } catch (err) {
      console.error('Error saving quotation:', err);
      setError(err.response?.data?.error || err.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} quotation`);
    } finally {
      setCreating(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED'
    }).format(amount || 0);
  };

  const { subtotal, taxTotal, grandTotal } = calculateTotals();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: isMobile ? 2 : 0, width: 0, minWidth: '100%', ...FORM_SX }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
      {/* Left column — form */}
      <Grid item xs={12} lg={8}>
      {/* Customer & Date Selection */}
      <Paper sx={{ p: 2.5, mb: 2 }}>
        {/* Customer type — same control as Create Work Order; walk-in is a Thaklis feature. */}
        {!thaklisDisabled && (
          <Box sx={{ mb: 2 }}>
            <ToggleButtonGroup value={customerType} exclusive fullWidth size="small"
              onChange={(e, v) => v && handleCustomerTypeChange(v)}>
              <ToggleButton value="organization" sx={{ py: 0.5, fontSize: '0.75rem', '&.Mui-selected': { bgcolor: 'action.selected', color: 'secondary.main' } }}>
                Organization
              </ToggleButton>
              <ToggleButton value="walkin" sx={{ py: 0.5, fontSize: '0.75rem', '&.Mui-selected': { bgcolor: 'action.selected', color: 'secondary.main' } }}>
                Walk-in Customer
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}

        <Grid container spacing={1.5}>
          {/* Organization Selection (only for organization type) */}
          {customerType === 'organization' && (
            <Grid item xs={12} md={mappedOrganizations.length > 1 ? 6 : 12}>
              <Autocomplete
                size="small"
                options={organizations}
                getOptionLabel={(option) => option.code ? `${option.name} (${option.code})` : option.name}
                isOptionEqualToValue={(o, v) => o?.id === v?.id}
                value={selectedOrganization}
                onChange={handleOrganizationChange}
                onInputChange={onOrganizationInputChange}
                filterOptions={organizationFilterOptions}
                loadingText="Searching..."
                noOptionsText="No organizations found"
                loading={loadingOrgs}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Customer Organization"
                    placeholder="Search by name or TRN..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingOrgs ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
          )}

          {/* Mapped Thaklis Organization — carried onto the work order and invoice. Like Create Work Order, it is
              only asked when the TC has more than one (a single one is auto-selected). */}
          {mappedOrganizations.length > 1 && (
          <Grid item xs={12} md={6}>
            <Autocomplete
              size="small"
              options={mappedOrganizations}
              getOptionLabel={(option) => `${option.name}${option.code ? ` (${option.code})` : ''}`}
              value={selectedMappedOrg}
              onChange={(e, value) => setSelectedMappedOrg(value)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              loading={loadingMappedOrgs}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Thaklis Organization"
                  placeholder="Search organizations..."
                  helperText="The Thaklis organization these applicants belong to"
                />
              )}
            />
          </Grid>
          )}

          {/* Organization Name — editable override (org mode only). Prefilled from the selected organization but
              typable so the TC can set a specific branch/contact label for this quotation. */}
          {customerType === 'organization' && (
            <Grid item xs={12} md={6}>
              <TextField
                size="small"
                label="Name on quotation"
                value={organizationNameOverride}
                onChange={(e) => setOrganizationNameOverride(e.target.value)}
                fullWidth
                placeholder="Organization name as shown on this quotation"
                helperText="Prefilled from the organization — edit if needed."
              />
            </Grid>
          )}

          {/* Applicant Name (always required) */}
          <Grid item xs={12} md={6}>
            <TextField
              size="small"
              label="Applicant Name"
              value={applicantName}
              onChange={(e) => setApplicantName(e.target.value)}
              fullWidth
              required
              placeholder="Enter applicant/customer name"
            />
          </Grid>

          {/* Email + Phone — always editable. In organization mode, prefilled from the selected org. */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              size="small"
              label="Email (Optional)"
              value={customerEmail}
              onChange={(e) => setCustomerEmail(e.target.value)}
              fullWidth
              type="email"
              placeholder="Enter email address"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              size="small"
              label="Phone (Optional)"
              value={customerPhone}
              onChange={(e) => setCustomerPhone(e.target.value)}
              fullWidth
              placeholder="Enter phone number"
            />
          </Grid>

          {/* Dates */}
          <Grid item xs={6} md={3}>
            <TextField
              size="small"
              label="Quotation Date"
              type="date"
              value={quotationDate}
              onChange={(e) => setQuotationDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              size="small"
              label="Valid Until"
              type="date"
              value={validUntil}
              onChange={(e) => setValidUntil(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Services Section — hidden in edit mode; use detail page inline add/remove */}
      {isEditMode ? (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Alert severity="info">
            To add or remove services, use the inline "Add Service" and delete
            buttons on the quotation detail page.
          </Alert>
        </Paper>
      ) : (
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'secondary.main' }}>
            Services ({lineItems.length})
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setServiceDialogOpen(true)}
            size="small"
            sx={{ textTransform: 'none', fontSize: '0.8125rem' }}
          >
            Add Service
          </Button>
        </Box>

        {lineItems.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No services added. Click "Add Service" to add services to this quotation.
            </Typography>
          </Box>
        ) : isMobile ? (
          /* Mobile Card View */
          <Stack spacing={2}>
            {lineItems.map((item) => (
              <Card key={item.id} variant="outlined">
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box flex={1}>
                      <Typography fontWeight={600} sx={{ fontSize: '0.9rem' }}>
                        {item.service_name}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography fontWeight={700} color="primary">
                        {formatCurrency(item.line_total)}
                      </Typography>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveLineItem(item.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  <Box display="flex" gap={2} mt={1} flexWrap="wrap">
                    <Typography variant="caption" color="text.secondary">
                      Service: {formatCurrency(item.service_charge)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Govt: {formatCurrency(item.govt_charge)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Tax: {formatCurrency((item.tax_value || 0) + (item.tax_on_markup || 0))}
                    </Typography>
                  </Box>
                  {item.markup_amount > 0 && (
                    <Typography variant="caption" color="success.main">
                      + Markup: {formatCurrency(item.markup_amount)}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            ))}
          </Stack>
        ) : (
          /* Desktop Table View */
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { backgroundColor: 'primary.main', fontWeight: 600, color: 'primary.contrastText', fontSize: '0.8rem', py: 1 } }}>
                  <TableCell>Service</TableCell>
                  <TableCell align="right">Service Chg</TableCell>
                  <TableCell align="right">Govt Chg</TableCell>
                  <TableCell align="right">Markup</TableCell>
                  <TableCell align="right">Tax</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lineItems.map((item) => {
                  const itemMarkup = parseFloat(item.markup_amount || 0);
                  const itemTaxOnMarkup = parseFloat(item.tax_on_markup || 0);
                  return (
                  <TableRow key={item.id} hover>
                    <TableCell sx={{ fontSize: '0.8125rem', py: 1 }}>
                      <Typography variant="body2" fontWeight={500}>{item.service_name}</Typography>
                      {item.service_description && (
                        <Typography variant="caption" color="text.secondary">
                          {item.service_description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.8125rem', py: 1 }}>{formatCurrency(item.service_charge)}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.8125rem', py: 1 }}>{formatCurrency(item.govt_charge)}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.8125rem', py: 1 }}>
                      {itemMarkup > 0 ? (
                        <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 500 }}>
                          {formatCurrency(itemMarkup)}
                        </Typography>
                      ) : (
                        <Typography variant="body2" sx={{ color: 'text.disabled' }}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.8125rem', py: 1 }}>
                      <Typography variant="body2">{formatCurrency(item.tax_value)}</Typography>
                      {itemTaxOnMarkup > 0 && (
                        <Typography sx={{ fontSize: '0.7rem', color: 'success.main' }}>
                          (+{formatCurrency(itemTaxOnMarkup)})
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.8125rem', py: 1 }}>
                      <Typography variant="body2" fontWeight={600}>{formatCurrency(item.line_total)}</Typography>
                      {itemMarkup > 0 && (
                        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                          (Base: {formatCurrency(item.original_line_total)})
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1 }}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveLineItem(item.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Totals */}
        {lineItems.length > 0 && (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Box sx={{ width: isMobile ? '100%' : 250 }}>
              <Box display="flex" justifyContent="space-between" mb={0.5}>
                <Typography variant="body2">Subtotal:</Typography>
                <Typography variant="body2">{formatCurrency(subtotal)}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={0.5}>
                <Typography variant="body2">Tax ({VAT_RATE_DISPLAY}):</Typography>
                <Typography variant="body2">{formatCurrency(taxTotal)}</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" fontWeight={600}>Grand Total:</Typography>
                <Typography variant="body2" fontWeight={700} color="primary">
                  {formatCurrency(grandTotal)}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </Paper>
      )}

      {/* Notes & Terms */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'secondary.main', mb: 1.5 }}>
          Notes & Terms
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Notes for Customer"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              rows={3}
              fullWidth
              placeholder="Any additional notes for the customer..."
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Terms & Conditions"
              value={termsAndConditions}
              onChange={(e) => setTermsAndConditions(e.target.value)}
              multiline
              rows={3}
              fullWidth
              placeholder="Payment terms, validity, etc..."
            />
          </Grid>
        </Grid>
      </Paper>

      </Grid>

      {/* Right column — summary + actions, like Create Work Order */}
      <Grid item xs={12} lg={4}>
        <Paper sx={{ p: 2, position: 'sticky', top: 20 }}>
          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'secondary.main', mb: 1.5 }}>
            Quotation Summary
          </Typography>
          {[
            ['Customer', (customerType === 'organization' ? (organizationNameOverride || selectedOrganization?.name) : 'Walk-in') || '—'],
            ['Applicant', applicantName.trim() || '—'],
            ['Services', lineItems.length],
            ['Valid until', validUntil ? new Date(`${validUntil}T00:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'],
          ].map(([label, val]) => (
            <Box key={label} display="flex" justifyContent="space-between" gap={2} mb={0.5}>
              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{label}</Typography>
              <Typography noWrap sx={{ fontSize: '0.75rem', fontWeight: 500, maxWidth: '65%' }}>{val}</Typography>
            </Box>
          ))}
          <Divider sx={{ my: 1 }} />
          <Box display="flex" justifyContent="space-between" mb={0.5}>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>Subtotal</Typography>
            <Typography sx={{ fontSize: '0.75rem' }}>{formatCurrency(subtotal)}</Typography>
          </Box>
          <Box display="flex" justifyContent="space-between" mb={0.5}>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>VAT ({VAT_RATE_DISPLAY})</Typography>
            <Typography sx={{ fontSize: '0.75rem' }}>{formatCurrency(taxTotal)}</Typography>
          </Box>
          <Divider sx={{ my: 1 }} />
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>Grand Total</Typography>
            <Typography sx={{ fontWeight: 700, color: 'secondary.main', fontSize: '1.1rem' }}>{formatCurrency(grandTotal)}</Typography>
          </Box>
          <Button
            fullWidth
            variant="contained"
            size="small"
            startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <SaveIcon sx={{ fontSize: 16 }} />}
            onClick={handleCreateQuotation}
            disabled={creating || lineItems.length === 0 || (!isEditMode && (!applicantName.trim() || (customerType === 'organization' && !selectedOrganization)))}
            sx={{ mt: 1.5, py: 1, fontSize: '0.75rem', textTransform: 'none' }}
          >
            {creating ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Quotation' : 'Create Quotation')}
          </Button>
          <Button fullWidth size="small" onClick={() => navigate('/quotations')}
            sx={{ mt: 0.5, fontSize: '0.75rem', textTransform: 'none', color: 'text.secondary' }}>
            Cancel
          </Button>
        </Paper>
      </Grid>
      </Grid>

      {/* Add Service Dialog */}
      <Dialog open={serviceDialogOpen} onClose={() => setServiceDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 600 }}>Add Service</DialogTitle>
        <DialogContent dividers sx={FORM_SX}>
          {/* Catalog or custom — same compact toggle as the customer type */}
          <ToggleButtonGroup value={isCustomService ? 'custom' : 'catalog'} exclusive fullWidth size="small"
            onChange={(e, v) => v && setIsCustomService(v === 'custom')} sx={{ mb: 2 }}>
            <ToggleButton value="catalog" sx={{ py: 0.5, fontSize: '0.75rem', '&.Mui-selected': { bgcolor: 'action.selected', color: 'secondary.main' } }}>
              From Catalog
            </ToggleButton>
            <ToggleButton value="custom" sx={{ py: 0.5, fontSize: '0.75rem', '&.Mui-selected': { bgcolor: 'action.selected', color: 'secondary.main' } }}>
              Custom Service
            </ToggleButton>
          </ToggleButtonGroup>

          {!isCustomService ? (
            /* Catalog service + markup on one row */
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={8}>
                <Autocomplete
                  size="small"
                  options={availableServices}
                  getOptionLabel={(option) => `${option.name || ''}${option.type === 'INTERNAL' ? ' [Internal]' : ''}`}
                  groupBy={(option) => option.type === 'INTERNAL' ? 'Internal Services' : 'Thaklis Services'}
                  value={selectedService}
                  onChange={(e, value) => setSelectedService(value)}
                  ListboxProps={{ sx: { '& .MuiAutocomplete-option': { fontSize: '0.8125rem' } } }}
                  renderInput={(params) => (
                    <TextField {...params} label="Service" placeholder="Search services..." autoFocus />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField size="small" label="Markup (AED)" type="number" value={markupPercent}
                  onChange={(e) => setMarkupPercent(e.target.value)} fullWidth placeholder="Optional" />
              </Grid>
            </Grid>
          ) : (
            /* Custom service — charge entered incl. VAT and split like InternalServiceDialog */
            <Grid container spacing={1.5}>
              <Grid item xs={12}>
                <TextField size="small" label="Service Name" value={customServiceName}
                  onChange={(e) => setCustomServiceName(e.target.value)} fullWidth autoFocus />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField size="small" label="Service Charge (incl. VAT)" type="number" value={customServiceCharge}
                  onChange={(e) => setCustomServiceCharge(e.target.value)} fullWidth helperText="5% VAT included" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField size="small" label="Govt Charge" type="number" value={customGovtCharge}
                  onChange={(e) => setCustomGovtCharge(e.target.value)} fullWidth helperText="No VAT" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField size="small" label="Markup (AED)" type="number" value={markupPercent}
                  onChange={(e) => setMarkupPercent(e.target.value)} fullWidth placeholder="Optional" />
              </Grid>
            </Grid>
          )}

          {/* Preview — one compact row */}
          {(selectedService || (isCustomService && customServiceCharge)) && (() => {
            // Catalog: exclusive sc + separate tax (already split). Custom: split on the fly.
            let serviceCharge, serviceTax, govtCharge;
            if (isCustomService) {
              const inclVat = parseFloat(customServiceCharge) || 0;
              serviceCharge = Number((inclVat / 1.05).toFixed(2));
              serviceTax = Number((inclVat - serviceCharge).toFixed(2));
              govtCharge = parseFloat(customGovtCharge) || 0;
            } else {
              serviceCharge = selectedService?.service_charge || 0;
              serviceTax = selectedService?.tax_on_service_charge || 0;
              govtCharge = selectedService?.govt_charge || 0;
            }
            const markupInclVat = parseFloat(markupPercent) || 0;
            const markupBase = Number((markupInclVat / 1.05).toFixed(2));
            const markupVat = Number((markupInclVat - markupBase).toFixed(2));
            const totalTax = serviceTax + markupVat;
            const lineTotal = serviceCharge + govtCharge + markupBase + totalTax;
            const cells = [
              ['Service charge', formatCurrency(serviceCharge)],
              ['Govt charge', formatCurrency(govtCharge)],
              ...(markupBase > 0 ? [['Markup', `+${formatCurrency(markupBase)}`]] : []),
              ['VAT', formatCurrency(totalTax)],
            ];
            return (
              <Box sx={{ mt: 2, px: 1.5, py: 1, bgcolor: 'action.hover', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                {cells.map(([label, value]) => (
                  <Box key={label}>
                    <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}>{label}</Typography>
                    <Typography sx={{ fontSize: '0.8125rem' }}>{value}</Typography>
                  </Box>
                ))}
                <Box sx={{ ml: 'auto', textAlign: 'right' }}>
                  <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}>Line total</Typography>
                  <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700, color: 'secondary.main' }}>{formatCurrency(lineTotal)}</Typography>
                </Box>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={() => setServiceDialogOpen(false)} sx={{ textTransform: 'none', fontSize: '0.8125rem' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddService}
            disabled={!isCustomService ? !selectedService : !customServiceName || !customServiceCharge}
            sx={{ textTransform: 'none', fontSize: '0.8125rem' }}
          >
            Add Service
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CreateQuotation;
